import { z } from 'zod';
/**
 * Fahrzeugversicherungen (KON-167, siehe docs/flotte.md): Verträge mit Laufzeit, Beitrag und Zahlweise, Schadenfreiheitsklassen und Kasko-Variante. Aus den Beiträgen entsteht die
 * Prognose der monatlichen Kosten. Beträge in ganzen Cent. Beiträge und Selbstbeteiligung sieht nur, wer das Recht `fleet.values` hat.
 */
export const INSURANCE_INTERVALS = ['monthly', 'quarterly', 'half_yearly', 'yearly'];
export const INSURANCE_INTERVAL_LABELS = { monthly: 'Monatlich', quarterly: 'Vierteljährlich', half_yearly: 'Halbjährlich', yearly: 'Jährlich' };
/** Monate je Zahlung */
export const INSURANCE_INTERVAL_MONTHS = { monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12 };
export const CASCO_KINDS = ['none', 'partial', 'full'];
export const CASCO_KIND_LABELS = { none: 'Nur Haftpflicht', partial: 'Teilkasko', full: 'Vollkasko (mit Teilkasko)' };
/** Schadenfreiheitsklassen: S, 0, M, ½ und 1 bis 35 */
export const SF_CLASSES = ['S', '0', 'M', '1/2', ...Array.from({ length: 35 }, (_, i) => String(i + 1))];
export const sfLabel = (sf) => (sf === null ? '–' : sf === '1/2' ? 'SF ½' : /^\d+$/.test(sf) ? `SF ${sf}` : `SF-Klasse ${sf}`);
/** Monatlicher Anteil eines Beitrags, auf ganze Cent gerundet */
export const insuranceMonthlyCents = (premiumCents, interval) => Math.round(premiumCents / INSURANCE_INTERVAL_MONTHS[interval]);
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
const cents = z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(0, 'Nicht negativ').max(1_000_000_00);
const sf = z.enum(SF_CLASSES, 'Bitte eine Schadenfreiheitsklasse wählen').nullable().optional().transform((v) => v ?? null);
/** Anlegen und Ändern eines Vertrags (beim Ändern gilt der ganze Satz) */
export const insuranceInput = z
    .object({
    insurer: z.string('Bitte den Versicherer angeben').trim().min(1, 'Bitte den Versicherer angeben').max(100),
    policyNumber: text(60),
    validFrom: z.iso.date('Bitte den Beginn angeben'),
    /** Ende der Laufzeit, leer bei unbefristeten Verträgen (zum Beispiel bei stillschweigender Verlängerung) */
    validUntil: z.iso.date('Ungültiges Datum').nullable().optional().transform((v) => v ?? null),
    premiumCents: cents.nullable().optional().transform((v) => v ?? null),
    premiumInterval: z.enum(INSURANCE_INTERVALS, 'Bitte die Zahlweise wählen').nullable().optional().transform((v) => v ?? null),
    liabilitySf: sf,
    cascoKind: z.enum(CASCO_KINDS).default('none'),
    cascoSf: sf,
    deductiblePartialCents: cents.nullable().optional().transform((v) => v ?? null),
    deductibleFullCents: cents.nullable().optional().transform((v) => v ?? null),
    note: text(500),
})
    .strict()
    .superRefine((v, ctx) => {
    const bad = (path, message) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.validUntil && v.validUntil < v.validFrom)
        bad('validUntil', 'Das Ende liegt vor dem Beginn.');
    if (v.premiumCents !== null && v.premiumInterval === null)
        bad('premiumInterval', 'Bitte die Zahlweise wählen.');
    if (v.premiumCents === null && v.premiumInterval !== null)
        bad('premiumCents', 'Bitte den Beitrag angeben.');
    if (v.cascoKind !== 'full' && v.cascoSf !== null)
        bad('cascoSf', 'Eine Vollkasko-Klasse gibt es nur bei Vollkasko.');
    if (v.cascoKind === 'none' && v.deductiblePartialCents !== null)
        bad('deductiblePartialCents', 'Ohne Kasko gibt es keine Selbstbeteiligung.');
    if (v.cascoKind !== 'full' && v.deductibleFullCents !== null)
        bad('deductibleFullCents', 'Die Selbstbeteiligung Vollkasko gibt es nur bei Vollkasko.');
});
export const INSURANCE_STATUS_LABELS = { current: 'Läuft', upcoming: 'Beginnt später', ended: 'Beendet' };
//# sourceMappingURL=insurance.js.map